import { HttpHeaders } from '@angular/common/http';
import { AppService } from '@services/app/app.service';
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class AuthRequestService {
  private cachedRequestToken: string;

  constructor(private appService: AppService) {}

  /**
   * applica agli headers passati in argomento i parametri necessari
   */
  applyAuthHeaderOnHeadersWhenNecessary(
    httpHeaders: HttpHeaders,
    options: { url: string }
  ): HttpHeaders {
    if (this.isAuthHeaderAdopted() && this.doesUrlNeedAuthHeader(options.url)) {
      httpHeaders = this.applyAuthHeaderOnHeaders(httpHeaders);
    }

    return httpHeaders;
  }

  /**
   * imposta l'header di autenticazione
   */
  applyAuthHeaderOnHeaders(httpHeaders: HttpHeaders): HttpHeaders {
    if (!(httpHeaders instanceof HttpHeaders)) {
      return httpHeaders;
    }

    const name = this.appService.getConfig().security.authHeaderName;
    const token = this.getRequestTokenFast();

    if (!!token) {
      httpHeaders = httpHeaders.set(name, token);
    }

    return httpHeaders;
  }

  /**
   * salva il token contenuto nell'header di autenticazione se necessario
   */
  saveAuthHeaderFromHeadersWhenNecessary(httpHeaders: HttpHeaders): void {
    if (this.isAuthHeaderAdopted()) {
      this.saveAuthHeaderFromHeaders(httpHeaders);
    }
  }

  /**
   * salva il token contenuto nell'header di autenticazione
   */
  saveAuthHeaderFromHeaders(httpHeaders: HttpHeaders): void {
    if (!(httpHeaders instanceof HttpHeaders)) {
      return;
    }

    const name = this.appService.getConfig().security.authHeaderName;
    const token = httpHeaders.get(name);

    this.saveRequestToken(token);
    this.updateCachedRequestToken({ forceUpdate: true });
  }

  /**
   * Determina se utilizzare il sistema di autenticazione
   * tramite header qinetic_authorization e request token
   */
  isAuthHeaderAdopted(): boolean {
    const demoMode = this.appService.isDemoActive();
    const devMode = this.appService.isDevMode();
    const hostEnv = this.appService.getHostEnv();
    const strategy = this.appService.getConfig().security.authStrategy;

    if (strategy === 'header') {
      return true;
    } else if (strategy === 'cookie') {
      return false;
    } else {
      return (
        !demoMode &&
        devMode &&
        (hostEnv === 'localhost' || hostEnv === 'develop')
      );
    }
  }

  /**
   * stabilisce se l'url necessita dell'auth header
   */
  doesUrlNeedAuthHeader(url: string): boolean {
    return !this.isUrlOutOfAuth(url);
  }

  /**
   * stabilisce se l'url è fuori l'area di autenticazione
   */
  isUrlOutOfAuth(url: string): boolean {
    const list = this.appService.getConfig().security.urlOutOfAuth || [];

    return list.findIndex(x => url.indexOf(x) > -1) > -1;
  }

  /**
   * restituisce il request token recuperandolo da quello presente su service
   * ed evitando di leggerlo dal local storage (utile se si effettua l'operazione molte volte di esguito)
   */
  private getRequestTokenFast(): string {
    this.updateCachedRequestToken();

    return this.cachedRequestToken;
  }

  /**
   * restituisce il token salvato su local storage
   */
  private getRequestToken(): string {
    const key = this.appService.getConfig().security.authStorageKeyName;

    return localStorage.getItem(key);
  }

  /**
   * imposta il request tokrn su local storage
   */
  private saveRequestToken(requestToken: string): void {
    const key = this.appService.getConfig().security.authStorageKeyName;

    if (!requestToken) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, requestToken);
    }
  }

  /**
   * aggiorna il request token sul service allineandolo con quello presente su localstorage
   */
  private updateCachedRequestToken(
    options: { forceUpdate?: boolean } = {}
  ): void {
    if (!!this.cachedRequestToken && !options.forceUpdate) {
      return;
    }

    this.cachedRequestToken = this.getRequestToken();
  }
}
